// ReferralUsersList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  limit as qLimit,
} from "firebase/firestore";

/* --------------------------- shared helpers --------------------------- */
function groupByLevel(users) {
  const result = { 1: [], 2: [], 3: [] };
  users.forEach((u) => {
    if (u.level === 1) result[1].push(u);
    else if (u.level === 2) result[2].push(u);
    else if (u.level === 3) result[3].push(u);
  });
  return result;
}

const statusColor = (status) => {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "declined") return "bg-red-100 text-red-700";
  return "bg-yellow-100 text-yellow-800"; // pending/other
};

/* ===== Currency settings (match AdminSales) ===== */
const UI_CURRENCY = "LKR";
const UI_LOCALE = "en-LK";
const money = (cur, n) =>
  `${cur || UI_CURRENCY} ${Number(n || 0).toLocaleString(UI_LOCALE)}`;

const monthKey = (d) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  return `${y}-${m}`;
};

async function upsertReferralLink(user = {}) {
  const code = String(user?.referralCode || "").trim();
  if (!code) return;

  await setDoc(
    doc(db, "referral_links", code),
    {
      ownerUserId: user.id || user.uid || "",
      referralCode: code,
      fullName: user.fullName || "",
      email: user.email || "",
      level: Number(user.level || 0) || null,
      referredBy: user.referredBy || "",
      status: user.status === "approved" ? "active" : "inactive",
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/* --------------------------- referral graph helpers --------------------------- */
async function getReferralUserByReferralCode(code) {
  if (!code) return null;
  const qy = query(
    collection(db, "referral_users"),
    where("referralCode", "==", code),
  );
  const snap = await getDocs(qy);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function getReferralUserById(id) {
  if (!id) return null;
  const s = await getDoc(doc(db, "referral_users", id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

async function getChildrenByReferredBy(code) {
  if (!code) return [];
  const qy = query(
    collection(db, "referral_users"),
    where("referredBy", "==", code),
  );
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Downline up to N levels via referralCode (depth-aware) */
async function getDownlineTree(rootReferralCode, depth = 3) {
  const result = { L1: [], L2: [], L3: [] };
  if (!rootReferralCode || depth <= 0) return result;

  // L1
  const L1 = await getChildrenByReferredBy(rootReferralCode);
  result.L1 = L1;

  // L2
  if (depth >= 2 && L1.length) {
    const L2 = (
      await Promise.all(L1.map((u) => getChildrenByReferredBy(u.referralCode)))
    ).flat();
    result.L2 = L2;

    // L3 (won't be used in a 3-level system, but supported)
    if (depth >= 3 && L2.length) {
      const L3 = (
        await Promise.all(
          L2.map((u) => getChildrenByReferredBy(u.referralCode)),
        )
      ).flat();
      result.L3 = L3;
    }
  }
  return result;
}

/** Ensure downline entries have .uid present (fetch full doc if missing). Also map .userId fallback. */
async function ensureUids(usersArr) {
  const out = [];
  for (const u of usersArr) {
    if (u?.uid || u?.userId) {
      out.push(u);
      continue;
    }
    const full = await getReferralUserById(u?.id);
    out.push(full || u);
  }
  return out;
}

/* --------------------------- SALES aggregation (robust) --------------------------- */
async function getPackageIncomeForUserAndTeam({
  userUid,
  teamUids = [],
  monthsBack = 3,
  fetchLimit = 2000,
}) {
  if (!userUid) {
    return {
      direct: { count: 0, total: 0, perMonth: {} },
      team: { count: 0, total: 0, perMonth: {} },
    };
  }

  const from = new Date();
  from.setMonth(from.getMonth() - monthsBack);
  from.setHours(0, 0, 0, 0);

  const qy = query(
    collection(db, "instructor_package_orders"),
    where("status", "==", "approved"),
    qLimit(fetchLimit),
  );
  const snap = await getDocs(qy);

  const direct = { count: 0, total: 0, perMonth: {} };
  const team = { count: 0, total: 0, perMonth: {} };

  const add = (bucket, amt, when) => {
    bucket.count += 1;
    bucket.total += Number(amt || 0);
    if (when >= from) {
      const key = monthKey(when);
      if (!bucket.perMonth[key]) bucket.perMonth[key] = { count: 0, total: 0 };
      bucket.perMonth[key].count += 1;
      bucket.perMonth[key].total += Number(amt || 0);
    }
  };

  const teamUidSet = new Set(teamUids.map(String));

  snap.docs.forEach((d) => {
    const o = d.data();
    const amt = Number(o?.payoutSnapshot?.affiliatePayoutApplied || 0);
    if (!(amt > 0)) return;
    const when =
      o.reviewedAt?.toDate?.() instanceof Date
        ? o.reviewedAt.toDate()
        : o.createdAt?.toDate?.() || new Date();
    const affiliateUid =
      o?.referralSnapshot?.affiliateUid ||
      o?.affiliateReferrerUid ||
      null;
    if (!affiliateUid) return;

    if (String(affiliateUid) === String(userUid)) {
      add(direct, amt, when);
      return; // don't double count
    }

    if (teamUidSet.has(String(affiliateUid))) add(team, amt, when);
  });

  return { direct, team };
}

/* --------------------------- UI atoms --------------------------- */
const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

const MiniBar = ({ value = 0, max = 1 }) => {
  const width = max > 0 ? Math.max(4, (value / max) * 100) : 0;
  return (
    <div className="h-2 rounded bg-gray-200 overflow-hidden">
      <div
        className="h-2 bg-blue-500"
        style={{ width: `${Math.min(width, 100)}%` }}
      />
    </div>
  );
};

const StatCard = ({ label, value, sub, glow = false }) => (
  <div
    className={`rounded-2xl p-4 border bg-white ${
      glow ? "shadow-[0_0_30px_rgba(59,130,246,0.15)]" : "shadow"
    }`}
  >
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-2xl font-extrabold mt-1">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

/* NEW: contact lines (pure UI) */
/* NEW: contact lines (pure UI) */
const ContactLines = ({ email, phone, whatsapp, compact = false }) => {
  const wa = whatsapp || phone;
  const clean = (s) => (s ? String(s).replace(/[^\d+]/g, "") : "");
  const waHref = wa
    ? `https://wa.me/${clean(wa.startsWith("+") ? wa.slice(1) : wa)}`
    : null;

  const Copy = ({ text, children }) => (
    <button
      onClick={() => navigator.clipboard?.writeText(String(text || ""))}
      className="underline decoration-dotted hover:opacity-80"
      title="Copy"
    >
      {children}
    </button>
  );

  const size = compact ? "text-[12px]" : "text-sm";
  const pad = compact ? "pt-1" : "pt-2";

  if (!email && !phone && !waHref) return null;

  return (
    <div className={`text-gray-600 ${pad} space-y-1 ${size}`}>
      {phone && (
        <div className="flex items-center gap-2">
          <span className="inline-flex px-2 py-[2px] rounded bg-gray-100">
            📞
          </span>
          <Copy text={phone}>{phone}</Copy>
        </div>
      )}
      {waHref && (
        <div className="flex items-center gap-2">
          <span className="inline-flex px-2 py-[2px] rounded bg-green-100">
            💬
          </span>
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            className="underline decoration-dotted hover:opacity-80"
          >
            WhatsApp
          </a>
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2">
          <span className="inline-flex px-2 py-[2px] rounded bg-blue-100">
            ✉️
          </span>
          <a
            href={`mailto:${email}`}
            className="underline decoration-dotted hover:opacity-80"
          >
            {email}
          </a>
        </div>
      )}
    </div>
  );
};

/* --------------------------- Table --------------------------- */
const UserTable = ({ title, users, onStatusClick, onOpenNetwork }) => (
  <div className="mb-10">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xl font-bold text-blue-700">{title}</h3>
      <Badge className="bg-blue-100 text-blue-700">{users.length}</Badge>
    </div>
    <div className="overflow-x-auto rounded-2xl border shadow-sm bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr className="font-semibold">
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Phone</th>
            <th className="px-3 py-2">Referral Code</th>
            <th className="px-3 py-2">Referred By</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Registered</th>
            <th className="px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-gray-50">
              <td className="px-3 py-3 font-medium">{u.fullName}</td>
              <td className="px-3 py-3">{u.email}</td>
              <td className="px-3 py-3">{u.phone}</td>
              <td className="px-3 py-3 font-mono text-xs">{u.referralCode}</td>
              <td className="px-3 py-3 font-mono text-xs">{u.referredBy}</td>
              <td className="px-3 py-3">
                <button
                  className={`px-2 py-1 rounded ${statusColor(
                    u.status,
                  )} cursor-pointer w-full`}
                  onClick={() => onStatusClick(u)}
                  title="View & update status"
                >
                  {u.status
                    ? u.status.charAt(0).toUpperCase() + u.status.slice(1)
                    : "Pending"}
                </button>
              </td>
              <td className="px-3 py-3">
                {u.createdAt && u.createdAt.seconds
                  ? new Date(u.createdAt.seconds * 1000).toLocaleString()
                  : ""}
              </td>
              <td className="px-3 py-3">
                <button
                  className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                  onClick={() => onOpenNetwork(u)}
                  title="Upline / Downline / Sales"
                >
                  Network
                </button>
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* --------------------------- KYC Modal (unchanged behavior, polished UI) --------------------------- */
const KycModal = ({ user, onClose, onApprove, onDecline }) => {
  const [showReason, setShowReason] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const handleApproveConfirm = async () => {
    try {
      setSubmitting(true);
      await onApprove(user);
      setShowApproveConfirm(false);
      setShowReason(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineSubmit = async () => {
    try {
      setSubmitting(true);
      await onDecline(user, declineReason);
      setShowReason(false);
      setShowApproveConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative overflow-hidden">
        {/* sticky header */}
        <div className="sticky top-0 bg-white/90 backdrop-blur px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-xl font-bold text-blue-700">KYC Details</h3>
          <button
            className="text-2xl text-gray-400 hover:text-gray-800"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* body (scrollable) */}
        <div className="p-6 overflow-y-auto max-h-[78vh]">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Name</div>
              <div className="font-semibold">{user.fullName}</div>
            </div>
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Email</div>
              <div className="font-semibold break-all">{user.email}</div>
            </div>
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Phone</div>
              <div className="font-semibold">{user.phone}</div>
            </div>
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">WhatsApp</div>
              <div className="font-semibold">
                {user.whatsapp || <span className="text-gray-400">-</span>}
              </div>
            </div>
            <div className="rounded-xl border p-3 bg-white sm:col-span-2">
              <div className="text-xs text-gray-500">NIC</div>
              <div className="font-semibold">{user.nic}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {user.imageUrl && (
              <div>
                <div className="text-xs mb-1 text-center">Profile Pic</div>
                <img
                  src={user.imageUrl}
                  alt="Profile"
                  className="rounded-xl w-full h-28 object-cover border"
                />
              </div>
            )}
            {user.nicFrontUrl && (
              <div>
                <div className="text-xs mb-1 text-center">NIC Front</div>
                <img
                  src={user.nicFrontUrl}
                  alt="NIC Front"
                  className="rounded-xl w-full h-28 object-cover border"
                />
              </div>
            )}
            {user.nicBackUrl && (
              <div>
                <div className="text-xs mb-1 text-center">NIC Back</div>
                <img
                  src={user.nicBackUrl}
                  alt="NIC Back"
                  className="rounded-xl w-full h-28 object-cover border"
                />
              </div>
            )}
            {user.bankFrontUrl && (
              <div>
                <div className="text-xs mb-1 text-center">Bank Front</div>
                <img
                  src={user.bankFrontUrl}
                  alt="Bank Front"
                  className="rounded-xl w-full h-28 object-cover border"
                />
              </div>
            )}
          </div>

          <div className="mt-4 border rounded-xl p-4 bg-white">
            <div className="font-bold text-blue-700 mb-2">Bank Details</div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <strong>Bank Name:</strong>{" "}
                {user.bankName || <span className="text-gray-400">-</span>}
              </div>
              <div>
                <strong>Account Number:</strong>{" "}
                {user.bankAccount || <span className="text-gray-400">-</span>}
              </div>
              <div>
                <strong>Branch:</strong>{" "}
                {user.bankBranch || <span className="text-gray-400">-</span>}
              </div>
              <div>
                <strong>Account Holder:</strong>{" "}
                {user.accountHolder || <span className="text-gray-400">-</span>}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              className="flex-1 py-2 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60"
              onClick={() => setShowApproveConfirm(true)}
              disabled={submitting}
            >
              Approve
            </button>
            <button
              className="flex-1 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              onClick={() => setShowReason(true)}
              disabled={submitting}
            >
              Decline
            </button>
            <button
              className="flex-1 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>

          {showReason && (
            <div className="mt-4 border rounded-xl p-3 bg-red-50">
              <label className="block font-semibold mb-1">
                Reason for Decline
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                className="w-full border rounded-lg p-2"
                placeholder="Enter the reason..."
                required
              />
              <div className="flex gap-2 mt-2">
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-60"
                  onClick={handleDeclineSubmit}
                  disabled={!declineReason.trim() || submitting}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
                <button
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg"
                  onClick={() => setShowReason(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showApproveConfirm && (
            <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
              <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border p-6">
                <h4 className="text-xl font-bold text-gray-900">
                  Approve this referral user?
                </h4>
                <p className="mt-2 text-gray-600">
                  This will approve the user&apos;s KYC and unlock their
                  referral account immediately.
                </p>
                <div className="mt-4 rounded-xl border bg-gray-50 p-4">
                  <div className="font-semibold text-gray-900">
                    {user.fullName || "Unnamed user"}
                  </div>
                  <div className="text-sm text-gray-600 break-all">
                    {user.email || "No email"}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60"
                    onClick={() => setShowApproveConfirm(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60"
                    onClick={handleApproveConfirm}
                    disabled={submitting}
                  >
                    {submitting ? "Approving..." : "Approve"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- NETWORK / SALES MODAL (95% width) --------------------------- */
const NetworkSalesModal = ({ user, onClose }) => {
  const [tab, setTab] = useState("sales");
  const [loading, setLoading] = useState(true);

  const [upline, setUpline] = useState({ parent: null, grand: null });
  const [downline, setDownline] = useState({ L1: [], L2: [] }); // depth-aware now

  const [directSales, setDirectSales] = useState({
    count: 0,
    total: 0,
    perMonth: {},
  });
  const [teamSales, setTeamSales] = useState({
    count: 0,
    total: 0,
    perMonth: {},
  });

  useEffect(() => {
    if (!user) return;

    (async () => {
      setLoading(true);

      const lvl = Number(user.level || 0);

      // Upline (max 2 hops in a 3-level system)
      const parent =
        lvl >= 2 ? await getReferralUserByReferralCode(user.referredBy) : null;
      const grand =
        lvl >= 3 && parent?.referredBy
          ? await getReferralUserByReferralCode(parent.referredBy)
          : null;
      setUpline({ parent, grand });

      // Downline depth by level:
      const depthWanted = lvl === 1 ? 2 : lvl === 2 ? 1 : 0;
      const dl = await getDownlineTree(user.referralCode, depthWanted);

      // ensure .uid/.userId exist
      const L1 = await ensureUids(dl.L1);
      const L2 = await ensureUids(dl.L2);
      setDownline({ L1, L2 });

      // Build team members per level policy
      let teamMembers = [];
      if (lvl === 1) teamMembers = [...L1, ...L2];
      else if (lvl === 2) teamMembers = [...L1];

      const teamUids = teamMembers
        .map((u) => String(u?.uid || u?.userId || ""))
        .filter(Boolean);
      // Ensure selected user's uid
      let userUid = String(user?.uid || user?.userId || "");
      if (!userUid) {
        const full = await getReferralUserById(user.id);
        userUid = String(full?.uid || full?.userId || "");
      }

      // Compute package income
      const { direct, team } = await getPackageIncomeForUserAndTeam({
        userUid,
        teamUids,
        monthsBack: 3,
        fetchLimit: 2000,
      });

      setDirectSales(direct);
      setTeamSales(team);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;

  const months = (() => {
    const now = new Date();
    const keys = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(now);
      d.setMonth(now.getMonth() - i);
      d.setDate(1);
      keys.push(monthKey(d));
    }
    return keys.reverse();
  })();

  const maxBar = Math.max(
    ...months.map((k) => teamSales.perMonth[k]?.total || 0),
    ...months.map((k) => directSales.perMonth[k]?.total || 0),
    1,
  );

  const lvl = Number(user.level || 0);
  // const teamSize =
  //   lvl === 1
  //     ? downline.L1.length + downline.L2.length
  //     : lvl === 2
  //     ? downline.L1.length
  //     : 0;

  const downlineGroups =
    lvl === 1
      ? [
          ["L1", downline.L1],
          ["L2", downline.L2],
        ]
      : lvl === 2
        ? [["L1", downline.L1]]
        : [];

  const downlineTitle =
    lvl === 1 ? "Downline (L1 / L2)" : lvl === 2 ? "Downline (L1)" : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl w-[95vw] max-w-[1400px] max-h-[92vh] overflow-hidden relative flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 pt-6 pb-3 border-b bg-white/90 backdrop-blur">
          <button
            className="absolute top-3 right-4 text-3xl text-gray-400 hover:text-gray-700"
            onClick={onClose}
          >
            &times;
          </button>

          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <h3 className="text-2xl font-extrabold text-blue-700 truncate">
                {user.fullName}
              </h3>
              <div className="text-gray-600 text-sm">
                <span className="font-mono">{user.referralCode}</span>{" "}
                <span className="mx-2">•</span> Level {user.level || "-"}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={statusColor(user.status)}>
                {user.status ? user.status.toUpperCase() : "PENDING"}
              </Badge>
              <Badge className="bg-blue-100 text-blue-700">
                Joined{" "}
                {user.createdAt?.seconds
                  ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                  : "-"}
              </Badge>
            </div>
          </div>

          {/* NEW: Contact details block (referral user contacts) */}
          <div className="mt-3 grid md:grid-cols-3 gap-3">
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Contacts</div>
              <ContactLines
                email={user.email}
                phone={user.phone}
                whatsapp={user.whatsapp}
              />
            </div>
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Direct Package Income</div>
              <div className="font-bold">
                {money(UI_CURRENCY, directSales.total)}
              </div>
              <div className="text-xs text-gray-500">
                {directSales.count} subscriptions
              </div>
            </div>
            <div className="rounded-xl border p-3 bg-white">
              <div className="text-xs text-gray-500">Team Package Income</div>
              <div className="font-bold">
                {money(UI_CURRENCY, teamSales.total)}
              </div>
              <div className="text-xs text-gray-500">
                {teamSales.count} subscriptions
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => setTab("network")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "network"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-blue-700 border hover:bg-blue-50"
              }`}
            >
              Network
            </button>
            <button
              onClick={() => setTab("sales")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "sales"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-blue-700 border hover:bg-blue-50"
              }`}
            >
              Sales
            </button>
            <button
              onClick={() => setTab("kyc")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "kyc"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-blue-700 border hover:bg-blue-50"
              }`}
            >
              KYC
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div className="p-6 overflow-y-auto max-h-[calc(92vh-140px)]">
          {loading ? (
            <div className="text-center text-gray-500">
              Loading network & sales…
            </div>
          ) : (
            <>
              {/* NETWORK TAB */}
              {/* NETWORK TAB */}
              {tab === "network" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Upline */}
                  <div className="lg:col-span-1">
                    <h4 className="font-bold text-gray-700 mb-2">Upline</h4>
                    <div className="space-y-3">
                      {Number(user.level || 0) >= 2 && (
                        <div className="rounded-xl border p-3 bg-white">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs text-gray-400">
                                Parent
                              </div>
                              <div className="font-semibold truncate">
                                {upline.parent ? upline.parent.fullName : "-"}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {upline.parent?.referralCode || ""}
                              </div>
                            </div>
                            <Badge className="bg-gray-100 text-gray-700 shrink-0">
                              {upline.parent
                                ? `L${upline.parent.level || "-"}`
                                : "—"}
                            </Badge>
                          </div>

                          {/* contact details */}
                          {upline.parent && (
                            <ContactLines
                              email={upline.parent.email}
                              phone={upline.parent.phone}
                              whatsapp={upline.parent.whatsapp}
                              compact
                            />
                          )}
                        </div>
                      )}

                      {Number(user.level || 0) >= 3 && (
                        <div className="rounded-xl border p-3 bg-white">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs text-gray-400">
                                Grandparent
                              </div>
                              <div className="font-semibold truncate">
                                {upline.grand ? upline.grand.fullName : "-"}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {upline.grand?.referralCode || ""}
                              </div>
                            </div>
                            <Badge className="bg-gray-100 text-gray-700 shrink-0">
                              {upline.grand
                                ? `L${upline.grand.level || "-"}`
                                : "—"}
                            </Badge>
                          </div>

                          {/* contact details */}
                          {upline.grand && (
                            <ContactLines
                              email={upline.grand.email}
                              phone={upline.grand.phone}
                              whatsapp={upline.grand.whatsapp}
                              compact
                            />
                          )}
                        </div>
                      )}

                      {Number(user.level || 0) === 1 && (
                        <div className="text-sm text-gray-400">
                          Level 1 has no upline.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Downline (depth-aware) */}
                  <div className="lg:col-span-2">
                    {downlineGroups.length ? (
                      <>
                        <h4 className="font-bold text-gray-700 mb-2">
                          {downlineTitle}
                        </h4>
                        <div
                          className={`grid md:grid-cols-${downlineGroups.length} gap-4`}
                        >
                          {downlineGroups.map(([label, arr]) => (
                            <div
                              key={label}
                              className="rounded-2xl border bg-white"
                            >
                              <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="font-semibold">{label}</div>
                                <Badge className="bg-blue-100 text-blue-700">
                                  {arr.length}
                                </Badge>
                              </div>

                              <div className="max-h-72 overflow-auto divide-y">
                                {arr.length ? (
                                  arr.map((d) => {
                                    const hasContact =
                                      d?.email || d?.phone || d?.whatsapp;
                                    return (
                                      <div
                                        key={d.id}
                                        className="px-4 py-3 text-sm"
                                      >
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="min-w-0">
                                            <div className="font-medium truncate">
                                              {d.fullName}
                                            </div>
                                            <div className="text-xs text-gray-500 font-mono">
                                              {d.referralCode}
                                            </div>
                                          </div>
                                          <div className="text-xs text-gray-500 shrink-0">
                                            L{d.level || "-"}
                                          </div>
                                        </div>

                                        {/* contact details for each downline member */}
                                        {hasContact && (
                                          <ContactLines
                                            email={d.email}
                                            phone={d.phone}
                                            whatsapp={d.whatsapp}
                                            compact
                                          />
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="px-4 py-6 text-center text-gray-400 text-sm">
                                    No members
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-2xl border bg-white p-4 text-gray-500">
                        Level 3 has no downline (cannot refer).
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SALES TAB */}
              {tab === "sales" && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-4 gap-4">
                    <StatCard
                      label="Direct Packages"
                      value={directSales.count}
                      sub={`Code: ${user.referralCode || "-"}`}
                      glow
                    />
                    <StatCard
                      label="Direct Income"
                      value={money(UI_CURRENCY, directSales.total)}
                    />
                    <StatCard
                      label="Team Packages"
                      value={teamSales.count}
                      sub={`Team size: ${
                        Number(user.level || 0) === 1
                          ? downline.L1.length + downline.L2.length
                          : Number(user.level || 0) === 2
                            ? downline.L1.length
                            : 0
                      }`}
                      glow
                    />
                    <StatCard
                      label="Team Income"
                      value={money(UI_CURRENCY, teamSales.total)}
                    />
                  </div>

                  {/* Last 3 months mini bars */}
                  <div className="rounded-2xl border bg-white p-4">
                    <div className="font-semibold mb-3">
                      Last 3 Months Package Income
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      {months.map((k) => (
                        <div key={k} className="border rounded-xl p-3">
                          <div className="text-xs text-gray-500 mb-1">{k}</div>
                          <div className="text-sm mb-1">
                            Direct:{" "}
                            {money(
                              UI_CURRENCY,
                              directSales.perMonth[k]?.total || 0,
                            )}{" "}
                            <span className="text-xs text-gray-400">
                              ({directSales.perMonth[k]?.count || 0})
                            </span>
                          </div>
                          <MiniBar
                            value={directSales.perMonth[k]?.total || 0}
                            max={maxBar}
                          />
                          <div className="text-sm mt-2 mb-1">
                            Team:{" "}
                            {money(
                              UI_CURRENCY,
                              teamSales.perMonth[k]?.total || 0,
                            )}{" "}
                            <span className="text-xs text-gray-400">
                              ({teamSales.perMonth[k]?.count || 0})
                            </span>
                          </div>
                          <MiniBar
                            value={teamSales.perMonth[k]?.total || 0}
                            max={maxBar}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-xs text-gray-400">
                    * Enforces 3-level MLM: L1→L2→L3; Level 3 cannot have
                    downline.
                  </div>
                </div>
              )}

              {/* KYC TAB (read-only mirror) */}
              {tab === "kyc" && (
                <div className="max-w-4xl">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl border p-4 bg-white">
                      <div className="text-xs text-gray-500">Name</div>
                      <div className="font-semibold">{user.fullName}</div>
                    </div>
                    <div className="rounded-xl border p-4 bg-white">
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="font-semibold break-all">
                        {user.email}
                      </div>
                    </div>
                    <div className="rounded-xl border p-4 bg-white">
                      <div className="text-xs text-gray-500">Phone</div>
                      <div className="font-semibold">{user.phone}</div>
                    </div>
                    <div className="rounded-xl border p-4 bg-white">
                      <div className="text-xs text-gray-500">NIC</div>
                      <div className="font-semibold">{user.nic}</div>
                    </div>
                  </div>

                  <div className="mt-4 grid sm:grid-cols-4 gap-4">
                    {user.imageUrl && (
                      <img
                        src={user.imageUrl}
                        alt="Profile"
                        className="rounded-xl border w-full h-32 object-cover"
                      />
                    )}
                    {user.nicFrontUrl && (
                      <img
                        src={user.nicFrontUrl}
                        alt="NIC Front"
                        className="rounded-xl border w-full h-32 object-cover"
                      />
                    )}
                    {user.nicBackUrl && (
                      <img
                        src={user.nicBackUrl}
                        alt="NIC Back"
                        className="rounded-xl border w-full h-32 object-cover"
                      />
                    )}
                    {user.bankFrontUrl && (
                      <img
                        src={user.bankFrontUrl}
                        alt="Bank Front"
                        className="rounded-xl border w-full h-32 object-cover"
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- main list --------------------------- */
const ReferralUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedUserForKYC, setSelectedUserForKYC] = useState(null);
  const [selectedUserForNetwork, setSelectedUserForNetwork] = useState(null);

  /* 🔎 NEW: search + sort state */
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("createdAt"); // 'createdAt' | 'name' | 'email' | 'level'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  const fetchUsers = async () => {
    setLoading(true);
    const snapshot = await getDocs(collection(db, "referral_users"));
    const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setUsers(rows);
    await Promise.all(rows.map((user) => upsertReferralLink(user).catch(() => null)));
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleStatusClick = (user) => setSelectedUserForKYC(user);
  const handleCloseKyc = () => setSelectedUserForKYC(null);

  const handleOpenNetwork = (user) => setSelectedUserForNetwork(user);
  const handleCloseNetwork = () => setSelectedUserForNetwork(null);

  // ---- NOTIFICATION-BASED APPROVE/DECLINE ----
  const handleApprove = async (user) => {
    await updateDoc(doc(db, "referral_users", user.id), { status: "approved" });
    await upsertReferralLink({ ...user, status: "approved" });
    await addDoc(collection(db, "referral_users", user.id, "notifications"), {
      type: "kyc",
      status: "approved",
      userId: user.id,
      recipientUid: user.id,
      message:
        "Your KYC has been approved! You now have full access to the platform.",
      createdAt: serverTimestamp(),
      read: false,
    });
    fetchUsers();
    setSelectedUserForKYC(null);
  };

  const handleDecline = async (user, reason) => {
    await updateDoc(doc(db, "referral_users", user.id), {
      status: "declined",
      declineReason: reason || "",
    });
    await upsertReferralLink({ ...user, status: "declined" });
    await addDoc(collection(db, "referral_users", user.id, "notifications"), {
      type: "kyc",
      status: "declined",
      userId: user.id,
      recipientUid: user.id,
      message: `Your KYC has been declined. Reason: ${
        reason || "No reason provided."
      }`,
      createdAt: serverTimestamp(),
      read: false,
    });
    fetchUsers();
    setSelectedUserForKYC(null);
  };

  /* ======================= NEW: filter + sort ======================= */
  function toTimeMs(u) {
    const ts = u?.createdAt;
    if (!ts) return 0;
    // Firestore Timestamp compat
    if (typeof ts?.toDate === "function") return ts.toDate().getTime();
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    // fallback if someone stored a JS Date or ms
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    return 0;
  }

  function safeStr(x) {
    return (x == null ? "" : String(x)).toLowerCase();
  }

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();

    // 1) filter (global)
    const filtered = users.filter((u) => {
      if (!q) return true;
      return (
        safeStr(u.id).includes(q) ||
        safeStr(u.fullName).includes(q) ||
        safeStr(u.email).includes(q) ||
        safeStr(u.phone).includes(q) ||
        safeStr(u.referralCode).includes(q) ||
        safeStr(u.referredBy).includes(q)
      );
    });

    // 2) sort (default: createdAt desc)
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "createdAt") {
        cmp = toTimeMs(a) - toTimeMs(b);
      } else if (sortKey === "name") {
        cmp = safeStr(a.fullName).localeCompare(safeStr(b.fullName));
      } else if (sortKey === "email") {
        cmp = safeStr(a.email).localeCompare(safeStr(b.email));
      } else if (sortKey === "level") {
        cmp = (Number(a.level || 0) || 0) - (Number(b.level || 0) || 0);
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [users, search, sortKey, sortDir]);

  // Group after filtering/sorting (so sections respect the query)
  const grouped = groupByLevel(filteredSorted);

  /* ======================= UI ======================= */
  return (
    <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-3xl font-black tracking-tight">Referral Users</h2>
          <div className="grid grid-flow-col gap-2">
            <Badge className="bg-blue-100 text-blue-700">
              L1 {grouped[1].length}
            </Badge>
            <Badge className="bg-indigo-100 text-indigo-700">
              L2 {grouped[2].length}
            </Badge>
            <Badge className="bg-purple-100 text-purple-700">
              L3 {grouped[3].length}
            </Badge>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Review referral users by level, inspect their network and recent
          sales, and manage KYC.
        </p>

        {/* 🔎 NEW: Search + Sort bar */}
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <div className="relative">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ID, name, email, phone, referral code or referred by…"
                className="w-full rounded-xl border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  title="Clear"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="flex-1 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Sort by"
            >
              <option value="createdAt">Sort: Created</option>
              <option value="name">Sort: Name (A–Z)</option>
              <option value="email">Sort: Email (A–Z)</option>
              <option value="level">Sort: Level (1→3)</option>
            </select>

            <button
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="rounded-xl border px-3 py-2 hover:bg-gray-50"
              title="Toggle ascending/descending"
            >
              {sortDir === "asc" ? "Asc ↑" : "Desc ↓"}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border bg-white p-6 text-center text-gray-500">
          Loading…
        </div>
      ) : (
        <>
          <UserTable
            title="Level 1 Users"
            users={grouped[1]}
            onStatusClick={handleStatusClick}
            onOpenNetwork={handleOpenNetwork}
          />
          <UserTable
            title="Level 2 Users"
            users={grouped[2]}
            onStatusClick={handleStatusClick}
            onOpenNetwork={handleOpenNetwork}
          />
          <UserTable
            title="Level 3 Users"
            users={grouped[3]}
            onStatusClick={handleStatusClick}
            onOpenNetwork={handleOpenNetwork}
          />
        </>
      )}

      {/* Existing KYC modal */}
      <KycModal
        user={selectedUserForKYC}
        onClose={handleCloseKyc}
        onApprove={handleApprove}
        onDecline={handleDecline}
      />

      {/* New Network + Sales modal (95% width) */}
      <NetworkSalesModal
        user={selectedUserForNetwork}
        onClose={handleCloseNetwork}
      />
    </div>
  );
};

export default ReferralUsersList;
